// Pure undo/redo stack shared by every page that offers Undo/Redo — the
// URL-hash history (charts/feedback/keywords) and the snapshot history
// (dashboards, catalog). Immutable: every operation returns a new history.

export const MAX_UNDO_HISTORY_ENTRIES = 100

export type UndoHistory<T> = {
  entries: T[]
  cursor: number
}

export type UndoStep<T> = {
  history: UndoHistory<T>
  /** The entry the cursor landed on, or null when already at that end. */
  target: T | null
}

export function emptyUndoHistory<T>(): UndoHistory<T> {
  return { entries: [], cursor: -1 }
}

export function canUndo<T>(history: UndoHistory<T>): boolean {
  return history.cursor > 0
}

export function canRedo<T>(history: UndoHistory<T>): boolean {
  return history.cursor < history.entries.length - 1
}

/**
 * Records `entry` at the cursor.
 *
 * - When `isSame` says it matches the entry at the cursor, that entry is
 *   amended in place (cursor and redo branch untouched). Landing on the state
 *   we are already at — an undo/redo restore, or a derived value settling —
 *   must neither grow the stack nor discard the redo branch.
 * - Otherwise the redo branch is discarded and the entry is pushed, dropping
 *   the oldest entry once the stack exceeds `MAX_UNDO_HISTORY_ENTRIES`.
 */
export function recordUndoEntry<T>(
  history: UndoHistory<T>,
  entry: T,
  isSame: (current: T, next: T) => boolean,
): UndoHistory<T> {
  const current = history.entries[history.cursor]
  if (current !== undefined && isSame(current, entry)) {
    const entries = [...history.entries]
    entries[history.cursor] = entry
    return { entries, cursor: history.cursor }
  }
  const pushed = [...history.entries.slice(0, history.cursor + 1), entry]
  const entries = pushed.slice(-MAX_UNDO_HISTORY_ENTRIES)
  return { entries, cursor: entries.length - 1 }
}

export function undoStep<T>(history: UndoHistory<T>): UndoStep<T> {
  if (!canUndo(history)) return { history, target: null }
  const cursor = history.cursor - 1
  return {
    history: { entries: history.entries, cursor },
    target: history.entries[cursor] ?? null,
  }
}

export function redoStep<T>(history: UndoHistory<T>): UndoStep<T> {
  if (!canRedo(history)) return { history, target: null }
  const cursor = history.cursor + 1
  return {
    history: { entries: history.entries, cursor },
    target: history.entries[cursor] ?? null,
  }
}
